import { thumbUrl, type CyberpunkPhoto } from '../../api/types';
import styles from './CyberpunkPhotoCard.module.css';

interface PhotoCardProps {
  photo: CyberpunkPhoto;
  index: number;
  onClick: () => void;
}

export default function CyberpunkPhotoCard({ photo, index, onClick }: PhotoCardProps) {
  const id = `NX-${7700 + index}`;
  const displayLabel = (photo.title || photo.filename).replace(/\.[^.]+$/, '').toUpperCase();
  // 日记日期：diary_date 优先，回退入库日期
  const diaryDate = photo.diary_date || photo.created_at.slice(0, 10);
  const hasDiary = Boolean(photo.title || photo.description);
  const status = index % 3 === 0 ? 'ARCHIVED' : index % 3 === 1 ? 'INDEXED' : 'ACTIVE';
  const threatLevel = ((index * 37 + 23) % 60) + 20; // deterministic based on index
  const threatBar = '█'.repeat(Math.floor(threatLevel / 10)) + '░'.repeat(10 - Math.floor(threatLevel / 10));
  // 真实分辨率优先（数据库登记值），未登记时显示 UNKNOWN
  const resolution =
    photo.width > 0 && photo.height > 0
      ? `${photo.width}×${photo.height}`
      : 'UNKNOWN';
  const cls = ['SPECIMEN', 'TARGET', 'ASSET', 'PERSONNEL'][index % 4];

  return (
    <div className={styles.card} onClick={onClick} data-status={status.toLowerCase()}>
      {/* Scanline overlay */}
      <div className={styles.scanline} />

      {/* Image area */}
      <div className={styles.imageWrapper}>
        <img
          src={thumbUrl(photo)}
          alt={photo.title || photo.filename}
          loading="lazy"
          className={styles.image}
        />
        {/* RGB glitch layer */}
        <div className={styles.glitchLayer} />
        {/* CRT scanlines */}
        <div className={styles.crtOverlay} />
      </div>

      {/* Data panel */}
      <div className={styles.dataPanel}>
        <div className={styles.dataRow}>
          <span className={styles.label}>FILE</span>
          <span className={styles.value}>{displayLabel}<span className={styles.blink}>_</span></span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.label}>LOG</span>
          <span className={styles.value}>
            {diaryDate}{hasDiary ? ' ✎' : ''}
          </span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.label}>ID</span>
          <span className={styles.value}>{id}</span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.label}>RES</span>
          <span className={styles.value}>{resolution}</span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.label}>STATUS</span>
          <span className={`${styles.value} ${styles.pulse} ${styles[status.toLowerCase()]}`}>● {status}</span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.label}>THREAT</span>
          <span className={styles.value}>
            <span className={styles.threatBar}>{threatBar}</span>
            <span className={styles.threatPercent}>{threatLevel}%</span>
          </span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.label}>CLASS</span>
          <span className={styles.value}>{cls}</span>
        </div>
      </div>

      {/* Barcode footer */}
      <div className={styles.barcode}>
        {Array.from({ length: 30 }, (_, i) => (
          <span key={i} className={styles.bar} style={{ opacity: ((i * 7 + index * 3) % 10) > 4 ? 1 : 0.3 }} />
        ))}
      </div>
    </div>
  );
}
